import { RouterProvider } from "@tanstack/react-router";
import { useMemo } from "react";
import type { ReactNode } from "react";
import { Button } from "@ec2/ui";
import { useT } from "@ec2/i18n/react";
import { AppProviders, useSession, type AppDeps } from "./state/session";
import { createAppRouter, type AppRouter } from "./router";
import { LoadingLine } from "./components/common";
import { UpdateBanner } from "./components/UpdateBanner";

/** Blocks the app until the silent anonymous session exists (backend contract). */
function BootGate({ children }: { children: ReactNode }) {
  const { status, retryBoot } = useSession();
  const t = useT();
  if (status === "booting") {
    return (
      <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center">
        <p className="text-center font-display text-lg font-bold tracking-[-0.03em] text-fg">{t("ec2.common.appName")}</p>
        <LoadingLine />
      </main>
    );
  }
  if (status === "error") {
    return (
      <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col items-center justify-center gap-3 px-6 text-center">
        <h1 className="font-display text-[21px] font-[650] text-fg">{t("ec2.boot.error.title")}</h1>
        <p className="text-base text-fg-secondary">{t("ec2.boot.error.body")}</p>
        <Button variant="primary" onClick={retryBoot} data-testid="ec-boot-retry">
          {t("ec2.common.retry")}
        </Button>
      </main>
    );
  }
  return <>{children}</>;
}

export function App({ deps, router }: { deps: AppDeps; router?: AppRouter }) {
  const appRouter = useMemo(() => router ?? createAppRouter(), [router]);
  return (
    <AppProviders deps={deps}>
      <BootGate>
        <RouterProvider router={appRouter} />
      </BootGate>
      <UpdateBanner />
    </AppProviders>
  );
}
